import { useState, useEffect, useRef } from "react";
import cytoscape from "cytoscape";
import { api } from "@/lib/api";
import { Pulse, ShieldCheck, Database, HardDrives } from "@phosphor-icons/react";

export default function SystemHealth() {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cyRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch admin health
  const fetchHealth = async () => {
    try {
      const res = await api.get("/admin/system-health");
      setHealthData(res.data);
    } catch (e) {
      console.error("Health fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 10000); // 10s auto-refresh
    return () => clearInterval(interval);
  }, []);

  // Initialize Cytoscape graph
  useEffect(() => {
    if (!healthData || !containerRef.current) return;

    if (!cyRef.current) {
      cyRef.current = cytoscape({
        container: containerRef.current,
        style: [
          {
            selector: 'node',
            style: {
              'background-color': '#2A2A35',
              'label': 'data(label)',
              'color': '#F8FAFC',
              'font-size': '11px',
              'font-family': 'monospace',
              'text-valign': 'center',
              'text-halign': 'center',
              'text-wrap': 'wrap',
              'text-max-width': '140px',
              'line-height': 1.4,
              // Fixed min-size so label always fits; nodes won't shrink below this
              'width': 'data(w)',
              'height': 'data(h)',
              'border-width': 2,
              'border-color': 'data(borderColor)',
              'shape': 'round-rectangle',
            }
          },
          {
            selector: 'edge',
            style: {
              'width': 2,
              'line-color': '#71717A',
              'target-arrow-color': '#71717A',
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              'opacity': 0.5
            }
          }
        ],
        layout: { name: 'preset' },
        userZoomingEnabled: false,
        userPanningEnabled: true,
        boxSelectionEnabled: false,
      });
    }

    const cy = cyRef.current;
    const elements = buildGraphData(healthData);

    cy.elements().remove();
    cy.add(elements);
    cy.fit(undefined, 60);

  }, [healthData]);

  const buildGraphData = (data) => {
    const nodes = [];
    const edges = [];
    const getStatusColor = (status) => {
      if (status === "healthy") return "#10B981";
      if (status === "degraded") return "#F59E0B";
      if (status === "disabled" || status === "offline") return "#71717A";
      return "#EF4444";
    };

    const node = (id, line1, line2, color, x, y, w = 190, h = 64) => ({
      data: { id, label: `${line1}\n${line2}`, line1, line2, borderColor: color, w, h },
      position: { x, y },
    });

    // 1. API Node (centre)
    const apiStatus = data.status || "unknown";
    nodes.push(node('api', 'FastAPI Backend', apiStatus, getStatusColor(apiStatus), 420, 130));

    // 2. Postgres + pgvector (single node — pgvector is a Postgres extension in v7.6)
    const dbOk = data.db === "connected";
    nodes.push(node('db', 'Postgres + pgvector', dbOk ? "connected" : "error", dbOk ? '#10B981' : '#EF4444', 420, 360));
    edges.push({ data: { id: 'api-db', source: 'api', target: 'db' } });

    // 3. Redis / Cache
    const redisOk = data.redis?.connected;
    const cacheStatus = redisOk ? "healthy" : "in-memory fallback";
    nodes.push(node('redis', 'Redis Cache', cacheStatus, redisOk ? '#10B981' : '#71717A', 200, 130, 190, 64));
    edges.push({ data: { id: 'api-redis', source: 'api', target: 'redis' } });

    // 5. LLM Providers — driven by llm_providers dict from the endpoint
    const providers = Object.entries(data.llm_providers || {});
    providers.forEach(([name, status], i) => {
      const pid = `llm_${name}`;
      nodes.push(node(pid, name.toUpperCase(), status, getStatusColor(status), 680, 80 + (i * 110), 180, 64));
      edges.push({ data: { id: `api-${pid}`, source: 'api', target: pid } });
    });

    return [...nodes, ...edges];
  };

  if (loading && !healthData) {
    return <div className="p-8 text-white">Loading system health...</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Pulse size={28} className="text-amber-500" />
        <h1 className="font-['Outfit'] text-2xl font-semibold text-white tracking-tight">System Telemetry</h1>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="jp-card p-5 border-l-4 border-l-amber-500">
          <div className="flex items-center gap-2 mb-2"><HardDrives size={16} className="text-amber-500" /><span className="text-sm font-semibold text-white">Backend Status</span></div>
          <div className="text-3xl font-mono text-white capitalize">{healthData?.status || "Unknown"}</div>
        </div>
        <div className="jp-card p-5 border-l-4 border-l-emerald-500">
          <div className="flex items-center gap-2 mb-2"><Database size={16} className="text-emerald-500" /><span className="text-sm font-semibold text-white">DB Size</span></div>
          <div className="text-3xl font-mono text-white">
            {healthData?.collections?.jobs?.documents ?? healthData?.collections?.jobs ?? 0}
          </div>
          <div className="text-xs text-zinc-500 mt-1">Total Jobs Indexed</div>
        </div>
        <div className="jp-card p-5 border-l-4 border-l-sky-500">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={16} className="text-sky-500" /><span className="text-sm font-semibold text-white">Circuit Breaker</span></div>
          <div className="text-xs text-zinc-400 mt-1 space-y-1">
            {healthData?.circuit_breakers && healthData.circuit_breakers.length > 0 ? (
              healthData.circuit_breakers.map((cb, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="uppercase font-medium">{cb.provider}</span>
                  <span className={cb.state === 'closed' ? 'text-emerald-400' : cb.state === 'open' ? 'text-red-400' : 'text-amber-400'}>{cb.state}</span>
                </div>
              ))
            ) : healthData?.llm_providers && Object.keys(healthData.llm_providers).length > 0 ? (
              // No CB events yet — show provider health from llm_providers
              Object.entries(healthData.llm_providers).map(([name, status]) => (
                <div key={name} className="flex justify-between">
                  <span className="uppercase font-medium">{name}</span>
                  <span className={status === 'healthy' ? 'text-emerald-400' : status === 'degraded' ? 'text-amber-400' : 'text-red-400'}>{status}</span>
                </div>
              ))
            ) : (
              <span className="text-zinc-600 italic">No providers configured</span>
            )}
          </div>
        </div>
      </div>

      <div className="jp-card h-[400px] relative overflow-hidden bg-[#0A0A0E]">
        <div className="absolute top-4 left-4 z-10 text-xs font-semibold uppercase tracking-widest text-zinc-500">Architecture Live Graph</div>
        <div ref={containerRef} className="w-full h-full" />
      </div>

    </div>
  );
}
